import React, { useState, useEffect, useMemo, useCallback } from "react";
import { mlmAPI } from "../services/api";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Card from "../components/Card";
import Sidebar from "../components/Sidebar";
import Button from "../components/Button";

const ArrowUpIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
  </svg>
);

const ArrowDownIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
  </svg>
);

const MetricCard = ({ title, value, subtext, change, isPositive }) => (
  <Card className="p-4">
    <div className="space-y-2">
      <p className="text-xs text-[var(--text-primary)] opacity-60 uppercase tracking-wider">
        {title}
      </p>
      <div className="flex items-end justify-between">
        <h3 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">{value}</h3>
        {change ? (
          <div
            className={`flex items-center gap-1 text-xs font-semibold ${
              isPositive ? "text-green-400" : "text-red-400"
            }`}
          >
            {isPositive ? <ArrowUpIcon className="w-3 h-3" /> : <ArrowDownIcon className="w-3 h-3" />}
            <span>{change}</span>
          </div>
        ) : null}
      </div>
      {subtext ? (
        <p className="text-xs text-[var(--text-primary)] opacity-40">{subtext}</p>
      ) : null}
    </div>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <Card className="p-4">
    <h3 className="text-sm md:text-base font-bold text-[var(--text-primary)] mb-4">{title}</h3>
    {children}
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[var(--bg-main)] border border-primary/30 rounded-lg p-3 shadow-xl">
        <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">{label}</p>
        <p className="text-sm font-bold text-[var(--text-primary)]">{payload[0].value}</p>
      </div>
    );
  }
  return null;
};

const MoneyTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const v = payload[0].value;
    return (
      <div className="bg-[var(--bg-main)] border border-primary/30 rounded-lg p-3 shadow-xl">
        <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">{label}</p>
        <p className="text-sm font-bold text-[var(--text-primary)]">
          ${typeof v === "number" ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v}
        </p>
      </div>
    );
  }
  return null;
};

function flattenLevelWise(levelWiseRaw) {
  const rows = [];
  Object.entries(levelWiseRaw || {}).forEach(([levelKey, members]) => {
    const levelNum = parseInt(String(levelKey).replace("level", ""), 10);
    const L = Number.isNaN(levelNum) ? 0 : levelNum;
    (members || []).forEach((m) => {
      rows.push({
        id: m.id,
        level: L,
        name: m.name,
        totalEarnings: Number(m.totalEarnings || 0),
        isActive: m.isActive !== false,
        createdAt: m.createdAt ? new Date(m.createdAt) : null,
        downlineCount: Number(m.downlineCount || 0),
      });
    });
  });
  return rows;
}

function monthKeyUTC(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthLabelUTC(d) {
  return d.toLocaleString(undefined, { month: "short", year: "numeric", timeZone: "UTC" });
}

/** Last `months` UTC months: new joins at depth `level`. */
function monthlyJoinsForLevel(flatMembers, level, months = 18) {
  const end = new Date();
  const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - (months - 1), 1, 0, 0, 0, 0));
  const map = new Map();
  flatMembers.forEach((m) => {
    if (m.level !== level || !m.createdAt || Number.isNaN(m.createdAt.getTime())) return;
    const k = monthKeyUTC(m.createdAt);
    map.set(k, (map.get(k) || 0) + 1);
  });
  const out = [];
  for (let d = new Date(start); d <= end; d.setUTCMonth(d.getUTCMonth() + 1)) {
    const k = monthKeyUTC(d);
    out.push({ month: monthLabelUTC(d), joins: map.get(k) || 0 });
  }
  return out;
}

/** Per month: sum of current totalEarnings for members at `level` who joined that month (cohort snapshot). */
function monthlyCohortEarningsForLevel(flatMembers, level, months = 18) {
  const end = new Date();
  const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - (months - 1), 1, 0, 0, 0, 0));
  const map = new Map();
  flatMembers.forEach((m) => {
    if (m.level !== level || !m.createdAt || Number.isNaN(m.createdAt.getTime())) return;
    const k = monthKeyUTC(m.createdAt);
    map.set(k, (map.get(k) || 0) + m.totalEarnings);
  });
  const out = [];
  for (let d = new Date(start); d <= end; d.setUTCMonth(d.getUTCMonth() + 1)) {
    const k = monthKeyUTC(d);
    out.push({ month: monthLabelUTC(d), volume: map.get(k) || 0 });
  }
  return out;
}

function monthlyActiveRatioForLevel(flatMembers, level, months = 18) {
  const end = new Date();
  const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - (months - 1), 1, 0, 0, 0, 0));
  const byMonth = new Map();
  flatMembers.forEach((m) => {
    if (m.level !== level || !m.createdAt || Number.isNaN(m.createdAt.getTime())) return;
    const k = monthKeyUTC(m.createdAt);
    if (!byMonth.has(k)) byMonth.set(k, { total: 0, active: 0 });
    const b = byMonth.get(k);
    b.total += 1;
    if (m.isActive) b.active += 1;
  });
  const out = [];
  for (let d = new Date(start); d <= end; d.setUTCMonth(d.getUTCMonth() + 1)) {
    const k = monthKeyUTC(d);
    const b = byMonth.get(k);
    const ratio = b && b.total > 0 ? Math.round((100 * b.active) / b.total) : 0;
    out.push({ month: monthLabelUTC(d), ratio });
  }
  return out;
}

const TIER_STYLES = [
  { dot: "from-[#CD7F32] to-[#8B4513]", line: "#22c55e", bar: "from-[#22c55e] to-[#16a34a]" },
  { dot: "from-[#C0C0C0] to-[#808080]", line: "#9ca3af", bar: "from-[#C0C0C0] to-[#808080]" },
  { dot: "from-[#FFD700] to-[#FFA500]", line: "#fbbf24", bar: "from-[#FFD700] to-[#FFA500]" },
];

function LevelDepthSection({ level, flatMembers, tierIndex }) {
  const style = TIER_STYLES[tierIndex % TIER_STYLES.length];
  const gradId = `lvlGrad-${level}`;
  const members = useMemo(
    () => flatMembers.filter((m) => m.level === level),
    [flatMembers, level],
  );
  const stats = useMemo(() => {
    const n = members.length;
    const sum = members.reduce((s, m) => s + m.totalEarnings, 0);
    const active = members.filter((m) => m.isActive).length;
    const avg = n ? sum / n : 0;
    return { n, sum, active, avg };
  }, [members]);

  const joinSeries = useMemo(() => monthlyJoinsForLevel(flatMembers, level), [flatMembers, level]);
  const cohortVolume = useMemo(() => monthlyCohortEarningsForLevel(flatMembers, level), [flatMembers, level]);
  const ratioSeries = useMemo(() => monthlyActiveRatioForLevel(flatMembers, level), [flatMembers, level]);

  const maxJoins = Math.max(1, ...joinSeries.map((r) => r.joins));
  const maxVol = Math.max(1, ...cohortVolume.map((r) => r.volume));

  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${style.dot}`} />
        <h2 className="text-lg md:text-xl font-bold text-[var(--text-primary)]">Level {level}</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <MetricCard
          title="Members at level"
          value={String(stats.n)}
          subtext={`Depth ${level} in your referral tree`}
        />
        <MetricCard
          title="Sum of member total earnings"
          value={`$${stats.sum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          subtext="Snapshot from profile totals"
        />
        <MetricCard
          title="Avg. earnings per member"
          value={`$${stats.avg.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          subtext={`${stats.active} active of ${stats.n}`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={`Level ${level} — new joins by month`}>
          {joinSeries.every((r) => r.joins === 0) ? (
            <p className="text-sm text-[var(--text-primary)] opacity-50 py-12 text-center">
              No join history in the last 18 months for this level.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={joinSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#22c55e" style={{ fontSize: "11px" }} />
                <YAxis stroke="#22c55e" style={{ fontSize: "11px" }} domain={[0, maxJoins]} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="joins" stroke={style.line} strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={`Level ${level} — cohort earnings by join month`}>
          {cohortVolume.every((r) => r.volume === 0) ? (
            <p className="text-sm text-[var(--text-primary)] opacity-50 py-12 text-center">
              No cohort earnings to chart for this level.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={cohortVolume}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#22c55e" style={{ fontSize: "11px" }} />
                <YAxis
                  stroke="#22c55e"
                  style={{ fontSize: "11px" }}
                  domain={[0, maxVol]}
                  tickFormatter={(v) => (v >= 1e3 ? `${(v / 1e3).toFixed(0)}k` : String(v))}
                />
                <Tooltip content={<MoneyTooltip />} />
                <Bar dataKey="volume" fill={`url(#${gradId})`} radius={[8, 8, 0, 0]} />
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity={1} />
                    <stop offset="100%" stopColor="#16a34a" stopOpacity={0.85} />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <ChartCard title={`Level ${level} — active % by join cohort (month)`}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={ratioSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="month" stroke="#22c55e" style={{ fontSize: "11px" }} />
              <YAxis
                stroke="#22c55e"
                style={{ fontSize: "11px" }}
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-[var(--bg-main)] border border-primary/30 rounded-lg p-3 shadow-xl">
                        <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">{label}</p>
                        <p className="text-sm font-bold text-[var(--text-primary)]">{payload[0].value}%</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Line type="monotone" dataKey="ratio" stroke={style.line} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

const MLMLevelDetails = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [botLevels, setBotLevels] = useState([]);
  const [tradingLevels, setTradingLevels] = useState([]);
  const [binaryLegVolumes, setBinaryLegVolumes] = useState({ leftUsd: 0, rightUsd: 0 });
  const [levelWiseRaw, setLevelWiseRaw] = useState({});
  const [teamAgg, setTeamAgg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [summary, levelWise, agg] = await Promise.all([
        mlmAPI.getCompensationPublicSummary(),
        mlmAPI.getLevelWiseReferrals(20),
        mlmAPI.getTeamAggregates().catch(() => null),
      ]);
      setBotLevels(Array.isArray(summary?.botAffiliateLevels) ? summary.botAffiliateLevels : []);
      setTradingLevels(
        Array.isArray(summary?.tradingAffiliateLevels) ? summary.tradingAffiliateLevels : [],
      );
      setBinaryLegVolumes(
        summary?.binaryLegVolumes && typeof summary.binaryLegVolumes === "object"
          ? {
              leftUsd: Number(summary.binaryLegVolumes.leftUsd || 0),
              rightUsd: Number(summary.binaryLegVolumes.rightUsd || 0),
            }
          : { leftUsd: 0, rightUsd: 0 },
      );
      setLevelWiseRaw(levelWise && typeof levelWise === "object" ? levelWise : {});
      setTeamAgg(agg);
    } catch (e) {
      setError(e?.error || e?.message || "Failed to load level details.");
      setBotLevels([]);
      setTradingLevels([]);
      setLevelWiseRaw({});
      setTeamAgg(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flatMembers = useMemo(() => flattenLevelWise(levelWiseRaw), [levelWiseRaw]);

  const overall = useMemo(() => {
    const totalMembers = flatMembers.length;
    const sumEarnings = flatMembers.reduce((s, m) => s + m.totalEarnings, 0);
    const active = flatMembers.filter((m) => m.isActive).length;
    return { totalMembers, sumEarnings, active };
  }, [flatMembers]);

  const powerUsd = Math.max(binaryLegVolumes.leftUsd, binaryLegVolumes.rightUsd);
  const weakUsd = Math.min(binaryLegVolumes.leftUsd, binaryLegVolumes.rightUsd);

  const levels4to20 = useMemo(() => {
    const rows = [];
    for (let L = 4; L <= 20; L += 1) {
      const ms = flatMembers.filter((m) => m.level === L);
      const n = ms.length;
      const sum = ms.reduce((s, m) => s + m.totalEarnings, 0);
      const act = ms.filter((m) => m.isActive).length;
      if (n > 0) rows.push({ level: L, n, sum, act });
    }
    return rows;
  }, [flatMembers]);

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 pt-16 md:p-8 md:pt-8 md:ml-64">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-1">
              Level details (L1–L20)
            </h1>
            <p className="text-sm text-[var(--text-primary)] opacity-60">
              Plan percentages from the live compensation config; charts and totals from your
              downline tree and binary leg deposits.
            </p>
          </div>
          <Button variant="secondary" onClick={load} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </div>

        {error ? (
          <Card className="p-4 mb-6 border border-red-500/30">
            <p className="text-red-400">{error}</p>
            <Button className="mt-3" onClick={load}>
              Retry
            </Button>
          </Card>
        ) : null}

        <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <h2 className="text-lg font-bold text-[var(--text-primary)] mb-2">Bot affiliate (L1–L20)</h2>
            <p className="text-sm text-[var(--text-primary)] opacity-70 mb-4">
              Percentages per level from the active compensation plan.
            </p>
            <div className="max-h-64 overflow-y-auto text-sm">
              {loading ? (
                <p className="opacity-50">Loading…</p>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-primary/20">
                      <th className="py-2">Level</th>
                      <th className="py-2">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {botLevels.map((pct, i) => (
                      <tr key={`b-${i}`} className="border-b border-primary/10">
                        <td className="py-1">L{i + 1}</td>
                        <td className="py-1">{pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
          <Card className="p-5">
            <h2 className="text-lg font-bold text-[var(--text-primary)] mb-2">Trading affiliate (L1–L20)</h2>
            <p className="text-sm text-[var(--text-primary)] opacity-70 mb-4">
              Percentages per level for trading-related affiliate volume.
            </p>
            <div className="max-h-64 overflow-y-auto text-sm">
              {loading ? (
                <p className="opacity-50">Loading…</p>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-primary/20">
                      <th className="py-2">Level</th>
                      <th className="py-2">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradingLevels.map((pct, i) => (
                      <tr key={`t-${i}`} className="border-b border-primary/10">
                        <td className="py-1">L{i + 1}</td>
                        <td className="py-1">{pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </div>

        <Card className="p-5 mb-8">
          <h2 className="text-lg font-bold text-[var(--text-primary)] mb-3">Binary leg deposit volume (USD)</h2>
          <p className="text-sm text-[var(--text-primary)] opacity-60 mb-4">
            Confirmed crypto deposits under each binary leg (subtree). Power / weaker use max / min for
            matching-style display.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-primary/20 p-4 bg-[var(--bg-main)]">
              <p className="text-sm font-semibold text-[var(--text-primary)]">Left leg</p>
              <p className="text-2xl font-bold text-primary mt-2">
                ${binaryLegVolumes.leftUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="rounded-xl border border-primary/20 p-4 bg-[var(--bg-main)]">
              <p className="text-sm font-semibold text-[var(--text-primary)]">Right leg</p>
              <p className="text-2xl font-bold text-primary mt-2">
                ${binaryLegVolumes.rightUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="rounded-xl border border-primary/20 p-4 bg-[var(--bg-main)] md:col-span-2">
              <p className="text-sm font-semibold text-[var(--text-primary)]">Power vs weaker (deposit USD)</p>
              <p className="text-lg text-[var(--text-primary)] mt-2">
                <span className="font-bold text-green-400">Power: </span>$
                {powerUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                <span className="mx-3 opacity-40">|</span>
                <span className="font-bold text-amber-400/90">Weaker: </span>$
                {weakUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </Card>

        <div className="mb-8">
          <h2 className="text-lg md:text-xl font-bold text-[var(--text-primary)] mb-4">Overall team metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              title="Total team members"
              value={loading ? "…" : String(overall.totalMembers)}
              subtext="All depths L1–L20 in your tree"
            />
            <MetricCard
              title="Sum of member total earnings"
              value={
                loading
                  ? "…"
                  : `$${overall.sumEarnings.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
              }
              subtext="From each member’s totalEarnings field"
            />
            <MetricCard
              title="Active members"
              value={loading ? "…" : String(overall.active)}
              subtext="isActive in your downline snapshot"
            />
          </div>
          {teamAgg ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <MetricCard
                title="Team deposits (aggregate)"
                value={`$${Number(teamAgg.totalTeamDeposits || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
                subtext="From team aggregates API"
              />
              <MetricCard
                title="Team revenue (aggregate)"
                value={`$${Number(teamAgg.totalTeamRevenue || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
                subtext="Earnings field aggregate"
              />
              <MetricCard
                title="Team principal (aggregate)"
                value={`$${Number(teamAgg.totalTeamPrincipal || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
                subtext="Principal-related aggregate"
              />
            </div>
          ) : null}
        </div>

        {[1, 2, 3].map((L, idx) => (
          <LevelDepthSection key={L} level={L} flatMembers={flatMembers} tierIndex={idx} />
        ))}

        <div className="mb-8">
          <h2 className="text-lg md:text-xl font-bold text-[var(--text-primary)] mb-4">
            Levels 4–20 (members present)
          </h2>
          {levels4to20.length === 0 ? (
            <Card className="p-4">
              <p className="text-sm text-[var(--text-primary)] opacity-60">
                No members at levels 4–20 in your tree yet.
              </p>
            </Card>
          ) : (
            <Card className="p-4 overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[480px]">
                <thead>
                  <tr className="border-b border-primary/20">
                    <th className="py-2 pr-4">Level</th>
                    <th className="py-2 pr-4">Members</th>
                    <th className="py-2 pr-4">Active</th>
                    <th className="py-2">Sum earnings</th>
                  </tr>
                </thead>
                <tbody>
                  {levels4to20.map((row) => (
                    <tr key={row.level} className="border-b border-primary/10">
                      <td className="py-2 pr-4">L{row.level}</td>
                      <td className="py-2 pr-4">{row.n}</td>
                      <td className="py-2 pr-4">{row.act}</td>
                      <td className="py-2">
                        $
                        {row.sum.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default MLMLevelDetails;
